import {
  DOCUMENT_KINDS,
  MAX_DOCUMENT_BYTES,
  documentKindLabel,
  formatBytes,
  isAllowedContentType,
  type DocumentKind,
  type DocumentView,
} from "@so/shared";
import { useRef, useState } from "react";
import { uploadToS3 } from "../api/client.js";
import { useDocuments, useRequestUpload } from "../api/queries.js";
import { Alert, Badge, Button, Card, ErrorNote, Field, Loading, Select, Spinner } from "./ui.js";

/**
 * Filenames are constrained server-side so they can safely form part of an S3
 * key. Rather than let the upload fail after the user has chosen a file, the
 * same rule is applied here and the offending characters replaced - with the
 * result shown, so nobody is surprised by a different name later.
 */
function tidyFilename(name: string): string {
  const cleaned = name
    .replace(/[^A-Za-z0-9 ._()-]/g, "-")
    .replace(/\.{2,}/g, ".")
    .replace(/^[^A-Za-z0-9]+/, "");

  return cleaned === "" ? "record.pdf" : cleaned.slice(0, 200);
}

function DocumentRow({ document }: { readonly document: DocumentView }) {
  return (
    <li className="border-line flex flex-wrap items-center gap-x-4 gap-y-2 border-b py-3 last:border-b-0">
      <div className="min-w-[200px] flex-1">
        <p className="text-[14.5px] font-medium">{document.filename}</p>
        <p className="text-ink-faint mt-0.5 text-[12.5px]">
          {documentKindLabel(document.kind)}
          <span className="mx-1.5">·</span>
          {formatBytes(document.size_bytes)}
          {document.note !== undefined && (
            <>
              <span className="mx-1.5">·</span>
              {document.note}
            </>
          )}
        </p>
      </div>

      {document.uploaded ? (
        <a href={document.download_url} download>
          <Button variant="secondary">Download</Button>
        </a>
      ) : (
        // Honest rather than a link that 404s: the record exists because an
        // upload was started, but the file never arrived.
        <Badge tone="caution">Upload did not finish</Badge>
      )}
    </li>
  );
}

export function Documents({
  appointmentId,
  canUpload,
  readOnlyReason,
}: {
  readonly appointmentId: string;
  readonly canUpload: boolean;
  readonly readOnlyReason?: string | undefined;
}) {
  const { data, isPending, error, refetch } = useDocuments(appointmentId);
  const requestUpload = useRequestUpload(appointmentId);

  const fileInput = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<DocumentKind>("imaging_report");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  async function handleFile(file: File) {
    setUploadError(undefined);

    if (file.size > MAX_DOCUMENT_BYTES) {
      setUploadError(
        `That file is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_DOCUMENT_BYTES)}.`,
      );
      return;
    }

    if (!isAllowedContentType(file.type)) {
      setUploadError(
        `${file.type === "" ? "That file type" : file.type} cannot be uploaded. Accepted: PDF, JPEG, PNG, TIFF, DICOM and plain text.`,
      );
      return;
    }

    setUploading(true);

    try {
      const ticket = await requestUpload.mutateAsync({
        filename: tidyFilename(file.name),
        content_type: file.type,
        size_bytes: file.size,
        kind,
      });

      // The headers must match what the API signed, byte for byte, or S3
      // refuses the upload.
      await uploadToS3(ticket.upload_url, ticket.required_headers, file);
      await refetch();
    } catch (caught) {
      setUploadError(
        caught instanceof Error ? caught.message : "The upload failed. Please try again.",
      );
    } finally {
      setUploading(false);
      if (fileInput.current !== null) fileInput.current.value = "";
    }
  }

  return (
    <Card className="p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[17px] font-semibold">Medical records</h2>
        {data !== undefined && data.documents.length > 0 && (
          <Badge>{data.documents.length} on file</Badge>
        )}
      </div>

      {isPending ? (
        <Loading label="Loading records" />
      ) : error ? (
        <ErrorNote error={error} />
      ) : data.documents.length === 0 ? (
        <p className="text-ink-soft text-[14.5px] leading-relaxed">
          {canUpload
            ? "Nothing uploaded yet. Imaging reports, pathology and discharge summaries all help the specialist give a useful opinion."
            : "The patient has not uploaded any records yet."}
        </p>
      ) : (
        <ul>
          {data.documents.map((document) => (
            <DocumentRow key={document.document_id} document={document} />
          ))}
        </ul>
      )}

      {canUpload && (
        <div className="border-line mt-5 border-t pt-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[190px] flex-1">
              <Field label="Type of record">
                <Select value={kind} onChange={(e) => setKind(e.target.value as DocumentKind)}>
                  {DOCUMENT_KINDS.map((value) => (
                    <option key={value} value={value}>
                      {documentKindLabel(value)}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Button
              variant="secondary"
              loading={uploading}
              onClick={() => fileInput.current?.click()}
            >
              {uploading ? "Uploading" : "Choose a file"}
            </Button>
          </div>

          <input
            ref={fileInput}
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.dcm,.txt,application/pdf,image/jpeg,image/png,image/tiff,application/dicom,text/plain"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void handleFile(file);
            }}
          />

          {uploadError !== undefined && (
            <div className="mt-3">
              <Alert tone="warn">{uploadError}</Alert>
            </div>
          )}

          <p className="text-ink-faint mt-3 text-[12.5px] leading-relaxed">
            Files go directly to encrypted storage and are only ever visible to you, the specialist
            reviewing your case, and your care coordinator. Up to{" "}
            {formatBytes(MAX_DOCUMENT_BYTES)} each.
          </p>
        </div>
      )}

      {!canUpload && readOnlyReason !== undefined && (
        <p className="text-ink-faint border-line mt-4 border-t pt-4 text-[12.5px]">
          {readOnlyReason}
        </p>
      )}

      {uploading && (
        <div className="text-ink-faint mt-3 flex items-center gap-2 text-[13px]">
          <Spinner className="h-4 w-4" />
          Uploading — this may take a moment for a large scan.
        </div>
      )}
    </Card>
  );
}
